import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ContactSecOne = ({ className }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    subject: '',
    message: ''
  });

  const [formMessage, setFormMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(null);
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormMessage('');
    
    // Add your Web3Forms access key to the form data
    const data = {
      ...formData,
      access_key: "6524626a-b495-4e3c-bc7f-24a187d51cca"
    };
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (result.success) {
        setFormMessage('✅ Message sent successfully! We will get back to you soon.');
        // Clear the form
        setFormData({
          name: '',
          email: '',
          number: '',
          subject: '',
          message: ''
        });
      } else {
        setFormMessage('❌ Failed to send message. Please try again.');
      }
    } catch (error) {
      setFormMessage('❌ An error occurred. Please try again later.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'far fa-map-marker-alt',
      title: 'Location',
      text: '932 Dogwood Road, Chapel Hill, North Carolina'
    },
    {
      icon: 'far fa-phone',
      title: 'Phone',
      links: [
        { href: 'tel:+16555778749', text: '+ 165-5577-8749' },
        { href: 'tel:+1653647488', text: '+ 165-3564-7488' }
      ]
    },
    {
      icon: 'far fa-envelope',
      title: 'Email',
      links: [
        { href: 'mailto:info@secur.com', text: 'info@secur.com' },
        { href: 'mailto:info@secur2.com', text: 'info@secur2.com' }
      ]
    }
  ];

  const serviceOptions = t('sections.contactSecOne.serviceOptions', { returnObjects: true, defaultValue: [
    'Security Consulting',
    'Security Training',
    'Data Security',
    'Security Assessment'
  ] });

  return (
    <div className={className} id="contact-sec">
      <div 
        className="shape-mockup bg-gradient-shape1 contact-bg-gradient1" 
        data-top="35%" 
        data-left="3%"
      />
      <div className="container">
        <div className="row gy-40 gx-60 justify-content-center align-items-center">
          <div className="col-lg-6">
            <div className="title-area">
              <span className="sub-title" data-cue="slideInUp">{t('sections.contactSecOne.sub_title', 'Get in Touch')}</span>
              <h2 className="sec-title" data-cue="slideInUp">
                {t('sections.contactSecOne.title', 'Ready to discuss your cybersecurity needs?')}
              </h2>
              <p className="sec-text" data-cue="slideInUp">
                {t('sections.contactSecOne.text', 'For over two decades, Secur has been at the forefront of cybersecurity, adapting to the ever-evolving threat landscape. Our seasoned professionals have a deep understanding of the latest attack vectors.')}
              </p>
            </div>
            <div className="contact-feature-wrap">
              <div className="contact-feature-wrap-details">
                {contactInfo.map((info, index) => (
                  <div className="contact-feature style2" data-cue="slideInLeft" key={index}>
                    <div className="box-icon">
                      <i className={info.icon} />
                    </div>
                    <div className="media-body">
                      <h4 className="box-title">{t(`sections.contactSecOne.contactInfo.${index}.title`, info.title)}</h4>
                      <p className="box-text">
                        {info.text ? (
                          t(`sections.contactSecOne.contactInfo.${index}.text`, info.text)
                        ) : (
                          info.links.map((link, linkIndex) => (
                            <React.Fragment key={linkIndex}>
                              <a href={link.href}>{t(`sections.contactSecOne.contactInfo.${index}.links.${linkIndex}.text`, link.text)}</a>
                              {linkIndex < info.links.length - 1 && ', '}
                            </React.Fragment>
                          ))
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="contact-form1" data-cue="slideInUp">
              <h2 className="contact-form-title mb-30">{t('sections.contactSecOne.form_title', 'How May We Help You!')}</h2>
              <form onSubmit={handleSubmit} className="input-label ajax-contact">
                <div className="row">
                  <div className="form-group col-sm-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t('sections.contactSecOne.placeholder.name', 'Name')}
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                    <i className="far fa-user text-theme" />
                  </div>
                  <div className="form-group col-sm-6">
                    <input
                      type="email"
                      className="form-control"
                      placeholder={t('sections.contactSecOne.placeholder.email', 'Email')}
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                    <i className="far fa-envelope text-theme" />
                  </div>
                  <div className="form-group col-sm-6">
                    <input
                      type="tel"
                      className="form-control"
                      placeholder={t('sections.contactSecOne.placeholder.number', 'Number')}
                      name="number"
                      id="number"
                      value={formData.number}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                    <i className="far fa-phone text-theme" />
                  </div>
                  <div className="form-group col-sm-6">
                    <select
                      name="subject"
                      id="subject"
                      className="form-select"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="" disabled>{t('sections.contactSecOne.select.service', 'Service')}</option>
                      {serviceOptions.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <i className="far fa-angle-down text-theme" />
                  </div>
                  <div className="form-group col-12">
                    <textarea
                      name="message"
                      id="message"
                      cols="30"
                      placeholder={t('sections.contactSecOne.placeholder.message', 'Write Message')}
                      rows="3"
                      className="form-control"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="form-btn col-12 mt-10">
                    <button type="submit" className="ot-btn w-100" disabled={isSubmitting}>
                      {isSubmitting ? t('sections.contactSecOne.sending', 'Sending...') : t('sections.contactSecOne.submit', 'Submit Now')} 
                      <i className="far fa-long-arrow-right ms-2" />
                    </button>
                  </div>
                </div>
                {formMessage && (
                  <p className="form-messages d-block mb-0 mt-3" style={{ 
                    color: formSuccess === true ? '#069845' : '#dc3545',
                    fontWeight: '500'
                  }}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSecOne;